import { z } from "zod";
import { PRESENTATION_ROLES } from "../../shared/presentation";

export const uiDocumentKindSchema = z.enum(["screen", "template", "component"]);
export const uiElementTypeSchema = z.enum([
  "image",
  "text",
  "button",
  "panel",
  "hitbox",
  "dynamic_character",
]);

export const characterRenderSourceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("current_princess") }).strict(),
  z.object({ type: z.literal("first_knight") }).strict(),
  z.object({ type: z.literal("specific_knight"), knightId: z.string().uuid() }).strict(),
  z.object({ type: z.literal("static_asset"), assetId: z.string().uuid() }).strict(),
]);

export const legacyUiSystemAssociationSchema = z.discriminatedUnion("system", [
  z.object({ system: z.literal("pvp"), surface: z.enum(["neighbourhood", "scout", "raid", "battle_log", "social"]) }).strict(),
  z.object({ system: z.literal("expedition"), expeditionId: z.string().uuid() }).strict(),
]);
export const presentationTemplateAssociationSchema = z.object({
  system: z.enum(["global","mission","black_keep","expedition","world_hunt","kingdom_threat","pvp","dialogue"]),
  role: z.enum(PRESENTATION_ROLES),
  ownerId: z.string().uuid().nullable(),
  isDefault: z.boolean(),
}).strict();
export const uiSystemAssociationSchema = z.union([presentationTemplateAssociationSchema,legacyUiSystemAssociationSchema]);
export const uiAnchorSchema = z.enum([
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "center",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
]);

export const uiActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("nothing") }).strict(),
  z
    .object({
      type: z.literal("go_to_screen"),
      targetDocumentId: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      type: z.literal("open_game_content"),
      contentType: z.string().min(1),
      targetId: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      type: z.literal("open_popup_template"),
      targetDocumentId: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      type: z.literal("continue_flow"),
      flowKey: z.string().regex(/^[a-z][a-z0-9_]*$/),
    })
    .strict(),
]);

export const runtimeUiDocumentSchema = z
  .object({
    id: z.string().uuid(),
    key: z.string().min(1),
    name: z.string().min(1),
    kind: uiDocumentKindSchema,
    backgroundAssetId: z.string().uuid().nullable(),
    description: z.string(),
    isCore: z.boolean().optional(),
    referenceScreenKey: z.string().min(1).nullable().optional(),
    systemAssociations: z.array(uiSystemAssociationSchema).optional(),
  })
  .strict();

export const runtimeUiElementSchema = z
  .object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    key: z.string().min(1),
    name: z.string().min(1),
    elementType: uiElementTypeSchema,
    sortOrder: z.number().int().nonnegative(),
    anchor: uiAnchorSchema,
    offsetX: z.number().finite(),
    offsetY: z.number().finite(),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
    opacity: z.number().min(0).max(1),
    visible: z.boolean(),
    locked: z.boolean(),
    assetId: z.string().uuid().nullable(),
    fixedText: z.string().nullable(),
    bindingKey: z.string().nullable(),
    action: uiActionSchema,
    characterSource: characterRenderSourceSchema.nullable().optional(),
  })
  .strict();

export type UiDocumentKind = z.infer<typeof uiDocumentKindSchema>;
export type UiElementType = z.infer<typeof uiElementTypeSchema>;
export type UiAnchor = z.infer<typeof uiAnchorSchema>;
export type UiAction = z.infer<typeof uiActionSchema>;
export type CharacterRenderSource = z.infer<typeof characterRenderSourceSchema>;
export type UiSystemAssociation = z.infer<typeof uiSystemAssociationSchema>;
export type RuntimeUiDocument = z.infer<typeof runtimeUiDocumentSchema>;
export type RuntimeUiElement = z.infer<typeof runtimeUiElementSchema>;

export type RuntimePreviewContext =
  | {
      kind?: "standard";
      knightKey?: string;
      buildingKey?: string;
      resourceKey?: string;
      bondKey?: string;
      cosmeticKey?: string;
      enemyKey?: string;
      modifierKey?: string;
      combatProfileKey?: string;
      rewardKey?: string;
      missionKey?: string;
      blackKeepFloor?: number;
      worldHuntKey?: string;
      kingdomThreatKey?: string;
      expeditionKey?: string;
      level?: number;
      playerDisplayName?: string;
      resourceAmount?: number;
      presentation?: import("../../shared/presentation").TemplateRenderContext;
    }
  | {
      kind: "world_map";
      slots: Array<{ id: string; x: number; y: number; label: string }>;
    }
  | {
      kind: "expedition_board";
      listings: Array<{ id: string; name: string; summary: string; entryPower: number; partySize: number }>;
    }
  | {
      kind: "expedition_map";
      expeditionKey: string;
      mapAssetId: string | null;
      nodes: Array<{ id: string; x: number; y: number; label: string }>;
      edges: Array<{ from: string; to: string }>;
    }
  | {
      kind: "pvp_neighbourhood";
      state: "normal" | "denounced";
      slots: Array<{
        id: string;
        x: number;
        y: number;
        label: string;
        denounced?: boolean;
      }>;
    };
